const MINUTES_PER_DAY = 1440;
const DAYS_PER_WEEK = 7;
const DAYS_PER_YEAR = 365;

/** Parses an interval written as "1y 2w 3d" into minutes, or null if it isn't one.
 *
 * Days are the smallest unit on offer. An hourly chore is indistinguishable from a daily one to a
 * status built on whole calendar days (see domain/scheduleUtils.MIN_INTERVAL_MINUTES), so "4h" is
 * rejected here rather than accepted and then rounded — telling the user their input is not a
 * schedule this app keeps beats silently storing something else. */
export const parseIntervalString = (str: string): number | null => {
  const trimmed = str.trim();
  if (!trimmed) return null;

  const parts = trimmed.split(/\s+/);
  let totalMinutes = 0;

  for (const part of parts) {
    const match = part.match(/^(\d+)([ywd])$/i);
    if (!match) return null;

    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();

    let multiplier: number;
    switch (unit) {
      case 'y': multiplier = DAYS_PER_YEAR * MINUTES_PER_DAY; break;
      case 'w': multiplier = DAYS_PER_WEEK * MINUTES_PER_DAY; break;
      case 'd': multiplier = MINUTES_PER_DAY; break;
      default: return null;
    }

    totalMinutes += value * multiplier;
  }

  return totalMinutes > 0 ? totalMinutes : null;
};

/** Renders minutes back as "1y 2w 3d", the form parseIntervalString reads.
 *
 * Rounded to whole days with a floor of one, so a row stored while hours were still offered reads
 * as "1d" rather than as the empty string this used to produce for anything under an hour. */
export const formatIntervalString = (minutes: number): string => {
  if (minutes <= 0) return '';

  const totalDays = Math.max(1, Math.round(minutes / MINUTES_PER_DAY));
  const years = Math.floor(totalDays / DAYS_PER_YEAR);
  const weeks = Math.floor((totalDays % DAYS_PER_YEAR) / DAYS_PER_WEEK);
  const days = (totalDays % DAYS_PER_YEAR) % DAYS_PER_WEEK;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years}y`);
  if (weeks > 0) parts.push(`${weeks}w`);
  if (days > 0) parts.push(`${days}d`);

  return parts.join(' ');
};

/** "Good morning" until noon, "Good afternoon" until six, "Good evening" after (and before five,
 * when "morning" would be a stretch). */
export const greetingFor = (date: Date): string => {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 18) return 'Good afternoon';
  return 'Good evening';
};
